import { Location } from "history";
import isNumber from "lodash-es/isNumber";
import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import { analyticsStoreEvent } from "@/mk2/actions";
import LeftMenu from "@/mk2/apps/blogs/components/LeftMenu";
import commentsSaga from "@/mk2/apps/blogs/containers/Comments/Comments.sagas";
import { BLOG_POST_MENU_MORE } from "@/mk2/apps/blogs/helpers";
import { PageRef } from "@/mk2/apps/forum/schemas";
import { forumUpdatedUrl } from "@/mk2/apps/forum/urls";
import { parsePageNumber } from "@/mk2/apps/forum/utils";
import {
    groupSharedDeleteGroupPostInit,
    groupSharedHidePostsOfAGroupInFeed,
    groupSharedInviteUser,
    groupSharedLeaveGroup,
    groupSharedPostSticky,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.actions";
import groupSharedReducer from "@/mk2/apps/groups/containers/GroupShared/GroupShared.reducers";
import groupSharedSaga from "@/mk2/apps/groups/containers/GroupShared/GroupShared.sagas";
import {
    getCommentComplaintsCounts,
    getModeratorUsersByGroupId,
    getPostsCommentsCount,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.selectors";
import { groupsSubscribedFeedUrl } from "@/mk2/apps/groups/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { AppName } from "@/mk2/containers/Application/Application";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { getComplaintsShownForBlogPosts } from "@/mk2/containers/Complaints/Complaints.selectors";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { memoizeOne } from "@/mk2/helpers/cache";
import { UserAgentParsed } from "@/mk2/helpers/detects";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    getRequestUserAgent,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import {
    BlogPostEntity,
    BlogPostSchema,
    GroupMemberEntity,
    GroupMemberSchema,
    MenuSelection,
    UserEntity,
    UsersByGroupId,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import { SEO_TITLE_GROUPS, TAB_FORUMS, TAB_GROUPS } from "@/mk/autogenerated/translation";
import { hasAds, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import GroupSubscribedFeed from "./GroupSubscribedFeed";
import {
    groupSubscribedFeedFetchTrigger,
    groupSubscribedFeedScrollToTrigger,
} from "./GroupSubscribedFeed.actions";
import {
    GROUP_SUBSCRIBED_FEED_MENU_MORE,
    GROUP_SUBSCRIBED_FEED_MENU_ORDERBY,
} from "./GroupSubscribedFeed.constants";
import reducer, {
    GroupSubscribedFeedPageState,
    GroupSubscribedFeedVirtualPageState,
    OrderBy,
    initialGroupSubscribedFeedPageState,
} from "./GroupSubscribedFeed.reducers";
import saga from "./GroupSubscribedFeed.sagas";
import {
    getGroupSubscribedFeedPageState,
    getGroupSubscribedFeedSearchState,
    getGroupSubscribedFeedState,
    getGroupSubscribedFeedVirtualPageState,
} from "./GroupSubscribedFeed.selectors";

export enum ScrollToPosition {
    FirstPost = "first",
    LastPost = "last",
}

interface OwnProps {}

interface StateProps {
    baseUrl: string;
    commentsCounts: { [postId: number]: number };
    complaintsCounts: { [postId: number]: number };
    complaintsOpened: boolean[];
    count: number;
    errorMessage: string;
    isIOS: boolean;
    loadingState: LoadingState;
    isMobile: boolean;
    moderatorUsersByGroupId: UsersByGroupId;
    numberOfPages: number;
    orderBy: OrderBy;
    pageRef: PageRef;
    permissions: string[];
    posts: BlogPostEntity[];
    feedLoaded: boolean;
    requestUser: UserEntity;
    userAgent: UserAgentParsed;
    groupsCount: number;

    isAdmin: boolean;
}

interface DispatchProps {
    onChangeSticky(postId: number, sticky: boolean);
    onDelete(postId: number);
    onGroupFeedMenuOrderBy();
    onGroupLeave(groupId: number, onSuccess);
    onGroupToggleMenuMore();
    onHideGroupInFeed(groupId: number, onSuccess);
    onInviteUser(groupId: number);
    onLoad(orderBy: OrderBy, pageRef: PageRef, currentUrl: Location, numCommentsBeforeLoad: number);
    onPostToggleMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number);
    onScrollTo(scrollto: ScrollToPosition);
    onStoreEvent(name: string, props: any);
}

type RouteProps = RouteComponentProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    orderby: OrderBy;
}

export default class GroupSubscribedFeedPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = parse(qs) || {};
        return {
            orderby: (qp.orderby as OrderBy) || "posts",
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const orderBy = parsedQuery.orderby;

            const pageRef = parsePageNumber(ownProps.location);

            const groupSubscribedFeedState = getGroupSubscribedFeedState(state);
            const groupPostsState = getGroupSubscribedFeedSearchState(state, orderBy);

            const moderators = getDenormalizedEntities<GroupMemberEntity>(
                state,
                GroupMemberSchema,
                groupSubscribedFeedState.moderators,
            );
            const moderatorUsers = moderators.map((groupMember) => groupMember.user);

            const permissions = getRequestPermissions(state);
            const requestUser = getRequestUser(state);
            const userAgent = getRequestUserAgent(state);

            const isAdmin = permissions.indexOf("forum.can_manage_forum") >= 0;
            const isModerator = moderatorUsers && isAuthenticatedAs(requestUser, moderatorUsers);

            const groupSubscribedFeedPageState:
                | GroupSubscribedFeedPageState
                | GroupSubscribedFeedVirtualPageState = isNumber(pageRef)
                ? getGroupSubscribedFeedPageState(state, orderBy, pageRef)
                : getGroupSubscribedFeedVirtualPageState(state, orderBy, pageRef);

            const postIds = isNumber(pageRef)
                ? (groupSubscribedFeedPageState as GroupSubscribedFeedPageState).postIds
                : [];

            const posts = getDenormalizedEntities<BlogPostEntity>(state, BlogPostSchema, postIds);
            const feedLoaded = postIds !== initialGroupSubscribedFeedPageState.postIds;

            const commentsLoadingState = groupSubscribedFeedState.commentsLoadingState;

            const commentsCounts = getPostsCommentsCount(state, posts);

            const complaintsCounts =
                isAdmin || isModerator
                    ? getCommentComplaintsCounts(state, postIds, commentsLoadingState)
                    : {};

            return {
                baseUrl: getRequestBaseUrl(state),
                commentsCounts,
                complaintsCounts,
                complaintsOpened: getComplaintsShownForBlogPosts(state, posts),
                count: groupPostsState.count,
                errorMessage: groupSubscribedFeedPageState.errorMessage,
                isIOS: getRequestDeviceIsIOS(state),
                loadingState: groupSubscribedFeedPageState.loadingState,
                isMobile: getRequestDeviceMobile(state),
                moderatorUsersByGroupId: getModeratorUsersByGroupId(moderators),
                numberOfPages: groupPostsState.numberOfPages,
                isAdmin,
                orderBy,
                pageRef,
                permissions,
                posts,
                feedLoaded,
                requestUser,
                userAgent,
                groupsCount: groupSubscribedFeedState.groupsCount,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onChangeSticky: groupSharedPostSticky,
            onDelete: groupSharedDeleteGroupPostInit,
            onGroupFeedMenuOrderBy: () => menuToggle(GROUP_SUBSCRIBED_FEED_MENU_ORDERBY),
            onGroupLeave: (groupId, onSuccess) => groupSharedLeaveGroup(groupId, false, onSuccess),
            onHideGroupInFeed: groupSharedHidePostsOfAGroupInFeed,
            onGroupToggleMenuMore: () => menuToggle(GROUP_SUBSCRIBED_FEED_MENU_MORE),
            onInviteUser: groupSharedInviteUser,
            onLoad: groupSubscribedFeedFetchTrigger,
            onPostToggleMenuMore: (postId: number) => menuToggle(BLOG_POST_MENU_MORE(postId)),
            onReport: complaintOpenTrigger,
            onScrollTo: groupSubscribedFeedScrollToTrigger,
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_GROUPS;
    }

    public getMetaDescription(props: Props): string {
        return null; // TBD
    }

    public render(props: Props): false | JSX.Element {
        return <GroupSubscribedFeed {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        const { location } = props;
        return <LeftMenu location={location} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Groups Feed - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: "Groups - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Groups - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, posts } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if (!posts || posts.length < 30) {
            return null;
        }

        return {
            zone: "Groups - Anchor Ad Leaderboard (Mobile)",
        };
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        return [
            <Btn key="forums" type={BtnType.Tab} label={TAB_FORUMS} link={forumUpdatedUrl} />,
            <Btn
                key="groups"
                type={BtnType.Tab}
                label={TAB_GROUPS}
                link={groupsSubscribedFeedUrl}
                active
            />,
        ];
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return null;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "groups",
            activeItem: "subscribed",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.groups.groupSubscribedFeed", reducer);
        store.injectSaga("containers.groups.groupSubscribedFeed", saga);

        store.injectReducer("containers.groups.groupShared", groupSharedReducer);
        store.injectSaga("containers.groups.groupShared", groupSharedSaga);

        store.injectSaga("containers.blogs.shared.comments", commentsSaga);
    }
}
